import { Contracts, Eye } from "@ignite-analytics/icons";
import { Stack, Typography, Paper, IconButton, Tooltip, Divider } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useFormikContext } from "formik";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router";

import { Answer, Question as TQuestion } from "../../globals/types";
import { useRefetchAssessment } from "../../hooks/useAssessmentDetail";
import useToken from "../../hooks/useToken";
import { useAlert } from "../../lib/Alerts";
import { authHeader, formsFetch, isClosed, saveAnswer, unwrap } from "../../lib/formsApi";
import { Files } from "../Files";
import { guessContentType } from "../Files/files";
import { initialValue } from "../Form/helpers";
import { AttachedFiles } from "../Question/AttachedFiles";
import { QuestionProps } from "../Question/interfaces";
import { ShapeIcon } from "../ShapeIcon";

type PreviewProps = { preview: true } | { preview?: false; assessmentId?: string };

type Props = {
    question: TQuestion;
    answer?: Answer;
    number: number;
    disabled?: boolean;
    setSubmitBlocked: React.Dispatch<React.SetStateAction<boolean>>;
    "data-testid"?: string;
} & PreviewProps;

const CodeOfConductQuestion: React.FC<Props> = ({ number, ...props }) => {
    const { alertUser } = useAlert();
    const { getFieldHelpers } = useFormikContext<Record<string, initialValue>>();
    const { setTouched: setFileNamesTouched, setValue: setFileNames } = getFieldHelpers<string[]>(
        `${props.question.id}.fileNames`
    );
    const { setTouched: setValueTouched } = getFieldHelpers<string[]>(`${props.question.id}.value`);
    const { id } = useParams<{ id: string }>();

    const { question, answer, setSubmitBlocked } = props;

    const { token } = useToken();
    const refetchAssessment = useRefetchAssessment();

    /** Save draft answer and upload files. */
    const saveDraft: QuestionProps["saveDraft"] = async (value, fileNames) => {
        if (props.preview) return "";

        // Auto-set boolean value based on file upload status
        let booleanValue = value ?? answer?.value;
        if (fileNames !== undefined) {
            if (fileNames.length > 0) {
                booleanValue = ["1"]; // Set to "Yes" when file is uploaded
            } else {
                booleanValue = []; // Clear answer when no files remain
            }
        }

        const body = { value: booleanValue, fileNames: fileNames ?? answer?.fileNames };
        const saved = await saveAnswer(token, id ?? "", question.id, body).catch(async (err) => {
            if (isClosed(err)) await refetchAssessment();
            throw err;
        });
        const answerId = saved.id;
        if (fileNames !== undefined || answer === undefined) setFileNames(saved.fileNames);
        await refetchAssessment();
        if (value || fileNames !== undefined) setValueTouched(true);
        if (fileNames) setFileNamesTouched(true);
        return answerId;
    };

    const getCodeOfConductFileName = () => {
        if (!question.attachedFileNames.length) return "";
        return question.attachedFileNames[0];
    };

    const handlePreview = async () => {
        const fileName = getCodeOfConductFileName();
        try {
            const { url } = unwrap(
                await formsFetch.POST("/api/files/download-url", {
                    headers: authHeader(token),
                    body: { referenceId: question.id, fileName },
                }),
                "create download url"
            );
            const response = await fetch(url, {
                headers: {
                    "Content-Type": guessContentType(fileName),
                    "X-Content-Type-Options": "nosniff",
                },
            });
            if (!response.ok) {
                throw new Error(`Download request failed with status ${response.status}`);
            }
            const blobUrl = window.URL.createObjectURL(await response.blob());
            window.open(blobUrl, "_blank", "noopener,noreferrer");
            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 10000);
        } catch (downloadError) {
            Sentry.captureException(downloadError, {
                extra: { fileName, questionId: question.id, context: "File download error" },
            });
            alertUser({
                value: <FormattedMessage defaultMessage="Could not open the file. Please try again." />,
                severity: "error",
            });
        }
    };

    return (
        <Stack width="100%" spacing={3}>
            <Stack spacing={2}>
                <Typography variant="textMd" fontWeight={500} color="textTextPrimary">
                    <FormattedMessage defaultMessage="1. Download the Code of Conduct document to be signed" />
                </Typography>
                <Typography variant="textSm" color="textTextHelper">
                    <FormattedMessage defaultMessage="Download this Code of Conduct document and sign it." />
                </Typography>

                {question.attachedFileNames.length > 0 && (
                    <Paper
                        variant="outlined"
                        sx={{
                            py: 1.5,
                            px: 2,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <ShapeIcon color="accent">
                                <Contracts fontSize="small" />
                            </ShapeIcon>
                            <Typography variant="textSm" sx={{ flex: 1 }}>
                                {getCodeOfConductFileName()}
                            </Typography>
                            <Tooltip title="Preview document">
                                <IconButton size="xsmall" color="secondary" onClick={handlePreview}>
                                    <Eye />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Paper>
                )}
                <AttachedFiles fileNames={question.attachedFileNames} questionId={question.id} />
            </Stack>
            <Divider />
            <Stack spacing={2}>
                <Typography variant="textMd" fontWeight={500} color="textTextPrimary">
                    <FormattedMessage defaultMessage="2. Upload your signed version of the Code of Conduct" />
                </Typography>
                <Typography variant="textSm" color="textTextHelper">
                    <FormattedMessage defaultMessage="Make sure you save and upload the signed version of the document." />
                </Typography>
                <Files
                    answer={answer}
                    saveDraft={saveDraft}
                    questionId={question.id}
                    uploadType={question.fileUpload}
                    disabled={props.disabled}
                    setSubmitBlocked={setSubmitBlocked}
                    multiple={false}
                    accept={".pdf"}
                />
            </Stack>
        </Stack>
    );
};

export default CodeOfConductQuestion;
